import { POLICY_CLS } from "../../../utils/constants";
import { EnumUtils, PolicyType } from "../../../utils/enums";
import { ApplyFunction } from "../ApplyFunction";
import { ApplyOn, ApplyOnUtils } from "../ApplyOn";
import { Condition, ConditionUtils } from "../Condition";
import { Policy } from "../cmds/Policy";
import { Process, ProcessUtils } from "../Process";
import { DetailedSafeObjectUtils, DetailedSafeObject } from "./IDetailedSafeObjecet";
import { analyzePolicyType } from "./DSOFactory";

export interface DetailedPolicy extends DetailedSafeObject {
  readonly type: PolicyType;
  readonly description: string;
  readonly name: string;
  readonly conditions: Condition[];
  readonly applyOn: ApplyOn[];
}

export const DetailedPolicyUtils = {
  create({
    type,
    description,
    name,
    conditions,
    applyOn,
    originalObjectSIG,
    isDeleted,
    isValid,
    applyFunction,
    id,
    parentGroup,
    SIGNATURE,
    comment,
    process,
    nestedObjects,
    source,
    destination,
    slot,
    timestamp,
    last_modified,
    pending_objects,
    affected_by,
    execute,
  }: {
    type?: PolicyType;
    description?: string;
    name: string;
    conditions: Condition[];
    applyOn: ApplyOn[];
    originalObjectSIG?: string;
    isDeleted?: boolean;
    isValid?: boolean;
    applyFunction?: ApplyFunction | null;
    id: string;
    parentGroup?: string;
    SIGNATURE?: string;
    comment?: string;
    process: Process;
    nestedObjects?: DetailedSafeObject[];
    source?: string;
    destination?: string;
    slot?: number;
    timestamp?: number;
    last_modified?: number;
    // pending_objects: DetailedSafeObject[];
    pending_objects?: DetailedPolicy[];
    affected_by?: DetailedSafeObject[];
    execute?: boolean;
  }): DetailedPolicy {
    const policyType = type ?? analyzePolicyType(applyOn);
    return {
      ...DetailedSafeObjectUtils.create({
        slot,
        timestamp,
        last_modified,
        affected_by,
        source,
        destination,
        originalObjectSIG,
        applyFunction,
        isDeleted,
        id,
        cls: POLICY_CLS,
        SIGNATURE,
        parentGroup,
        comment,
        isValid,
        nestedObjects,
        process,
        pending_objects,
        execute,
      }),
      type: policyType,
      description,
      name,
      conditions,
      applyOn,
    };
  },

  empty(): DetailedPolicy {
    const tms = Date.now();
    return DetailedPolicyUtils.create({
      slot: -1,
      last_modified: tms,
      affected_by: [],
      timestamp: tms,
      source: "",
      destination: "",
      SIGNATURE: "",
      applyFunction: null,
      applyOn: [],
      comment: "",
      conditions: [],
      description: "",
      id: "",
      isDeleted: false,
      isValid: false,
      name: "",
      nestedObjects: [],
      originalObjectSIG: "",
      parentGroup: "",
      process: ProcessUtils.empty(),
      type: PolicyType.Error,
      pending_objects: [],
      execute: true,
    });
  },

  toJson(policy: DetailedPolicy): string {
    return JSON.stringify(DetailedPolicyUtils.toMap(policy));
  },

  toMap(policy: DetailedPolicy): Record<string, any> {
    const json = DetailedSafeObjectUtils.toMap(policy);
    json["object"] = {
      ...json["object"],
      destination: policy.destination,
      source: policy.source,
      name: policy.name,
      description: policy.description,
      conditions: policy.conditions.map((c) => ConditionUtils.toMap(c)),
      applyOn: policy.applyOn.map((c) => ApplyOnUtils.toMap(c)),
    };
    return json;
  },

  copyWith(
    policy: DetailedPolicy,
    {
      type,
      id,
      description,
      name,
      conditions,
      parentGroup,
      applyOn,
      SIGNATURE,
      comment,
      process,
      nestedObjects,
      isValid,
      isDeleted,
      applyFunction,
      originalObjectSIG,
      source,
      destination,
      slot,
      timestamp,
      last_modified,
      pending_objects,
      affected_by,
      execute,
    }: {
      type?: PolicyType;
      id?: string;
      description?: string;
      name?: string;
      conditions?: Condition[];
      parentGroup?: string;
      applyOn?: ApplyOn[];
      SIGNATURE?: string;
      comment?: string;
      process?: Process;
      nestedObjects?: DetailedSafeObject[];
      isValid?: boolean;
      isDeleted?: boolean;
      applyFunction?: ApplyFunction | null;
      originalObjectSIG?: string;
      source?: string;
      destination?: string;
      slot?: number;
      timestamp?: number;
      last_modified?: number;
      pending_objects?: DetailedPolicy[];
      affected_by?: DetailedSafeObject[];
      execute?: boolean;
    }
  ): DetailedPolicy {
    return DetailedPolicyUtils.create({
      slot: slot ?? policy.slot,
      last_modified: last_modified ?? policy.last_modified,
      timestamp: timestamp ?? policy.timestamp,
      source: source ?? policy.source,
      destination: destination ?? policy.destination,
      originalObjectSIG: originalObjectSIG ?? policy.originalObjectSIG,
      applyFunction: applyFunction ?? policy.applyFunction,
      isDeleted: isDeleted ?? policy.isDeleted,
      type: type ?? policy.type,
      id: id ?? policy.id,
      description: description ?? policy.description,
      name: name ?? policy.name,
      conditions: conditions
        ? conditions.map((c) => ConditionUtils.fromMap(ConditionUtils.toMap(c)))
        : policy.conditions.map((c) => ConditionUtils.fromMap(ConditionUtils.toMap(c))),
      parentGroup: parentGroup ?? policy.parentGroup,
      applyOn: applyOn ?? policy.applyOn,
      SIGNATURE: SIGNATURE ?? policy.SIGNATURE,
      comment: comment ?? policy.comment,
      process: process ?? policy.process,
      nestedObjects: nestedObjects ?? policy.nestedObjects,
      isValid: isValid ?? policy.isValid,
      pending_objects: pending_objects ?? (policy.pending_objects as DetailedPolicy[]) ?? [],
      affected_by: affected_by ?? policy.affected_by,
      execute: execute ?? policy.execute,
    });
  },

  copyWithFromPolicy(
    policy: DetailedPolicy,
    { policy: sourcePolicy }: { policy: Policy }
  ): DetailedPolicy {
    return DetailedPolicyUtils.copyWith(policy, {
      applyOn: sourcePolicy.applyOn,
      conditions: sourcePolicy.conditions,
      description: sourcePolicy.description,
      name: sourcePolicy.name,
      SIGNATURE: sourcePolicy.SIGNATURE,
      comment: sourcePolicy.comment,
      parentGroup: sourcePolicy.parentGroup,
      id: sourcePolicy.id,
      type: sourcePolicy.type,
      timestamp: sourcePolicy.timestamp ?? Date.now(),
    });
  },

  policyTypeIcon(policy: DetailedPolicy): string {
    switch (policy.type) {
      case PolicyType.Groups:
        return "group";
      case PolicyType.Transaction:
        return "monetization_on";
      default:
        return "history_edu_outlined";
    }
  },

  fromPolicy({
    policy,
    SIGNATURE,
    type,
    process,
    nestedObjects,
    isValid,
    isDeleted,
    applyFunction,
    originalObjectSIG,
    source,
    destination,
  }: {
    policy: Policy;
    SIGNATURE?: string;
    type?: PolicyType;
    process?: Process;
    nestedObjects?: DetailedSafeObject[];
    isValid?: boolean;
    isDeleted?: boolean;
    applyFunction?: ApplyFunction | null;
    originalObjectSIG?: string;
    source?: string;
    destination?: string;
  }): DetailedPolicy {
    const tms = Date.now();
    return DetailedPolicyUtils.create({
      name: policy.name,
      description: policy.description,
      conditions: policy.conditions,
      applyOn: policy.applyOn,
      comment: policy.comment,
      parentGroup: policy.parentGroup,
      SIGNATURE: SIGNATURE ?? policy.SIGNATURE,
      id: policy.id,
      slot: -1,
      last_modified: tms,
      affected_by: [],
      timestamp: policy.timestamp ?? tms,
      source: source ?? "",
      destination: destination ?? "",
      applyFunction: applyFunction ?? null,
      isDeleted: isDeleted ?? false,
      isValid: isValid ?? false,
      nestedObjects: nestedObjects ?? [],
      originalObjectSIG: originalObjectSIG ?? "",
      process: process ?? ProcessUtils.empty(),
      type: type ?? PolicyType.Error,
      pending_objects: [],
      execute: policy.execute,
    });
  },

  isDetailedPolicy(obj: any): obj is DetailedPolicy {
    return (
      DetailedSafeObjectUtils.isDetailedSafeObject(obj) &&
      obj.cls === POLICY_CLS &&
      "conditions" in obj &&
      Array.isArray(obj.conditions) &&
      obj.conditions.length > 0 &&
      "applyOn" in obj &&
      Array.isArray(obj.applyOn) &&
      "type" in obj &&
      Object.values(PolicyType).includes(obj.type as PolicyType) &&
      "description" in obj &&
      typeof obj.description === "string" &&
      "name" in obj &&
      typeof obj.name === "string"
    );
  },
};
